import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { getPreferences, updatePreferenceDirect } from '../hooks/usePreferences';

export type OverlayId =
  | 'cmeInfo'
  | 'signalInfo'
  | 'accountStats'
  | 'pinningVolume'
  | 'wholeCandleContinuation'
  | 'confidence'
  | 'fairPriceCoach'
  | 'diamondInfo'
  | 'fairPriceStatus'
  | 'diamondStats'
  | 'diamondSignalExcursion'
  | 'gexCoach';

export interface OverlayDefinition {
  id: OverlayId;
  label: string;
  description: string;
}

export const OVERLAY_DEFINITIONS: OverlayDefinition[] = [
  { id: 'cmeInfo', label: 'CME Info', description: 'Market status and session info' },
  { id: 'signalInfo', label: '24H Signals', description: 'Signal statistics for last 24 hours' },
  { id: 'accountStats', label: 'Account Stats', description: 'Account balance and P&L' },
  { id: 'pinningVolume', label: 'Pinning Volume', description: 'Volume spike analysis' },
  { id: 'wholeCandleContinuation', label: 'Whole Candle', description: 'WCC strategy analysis' },
  { id: 'confidence', label: 'Confidence', description: 'Signal confidence scoring' },
  { id: 'fairPriceCoach', label: 'Fair Price Coach', description: 'Live plain-English commentary on the JJ Simon setup' },
  { id: 'diamondInfo', label: 'Diamond Info', description: 'Why the crosshair candle is (or is not) a diamond' },
  { id: 'fairPriceStatus', label: 'Fair Price Status', description: 'Live phase, arming and distance chip' },
  { id: 'diamondStats', label: '24H Diamonds', description: 'Simulated wins/losses and opportunities for the last 24 hours' },
  { id: 'diamondSignalExcursion', label: 'Diamond MFE/MAE', description: 'Trailing 30 trades: how far each ran in favour, and how far it went against first' },
  { id: 'gexCoach', label: 'Gamma Coach', description: 'Gamma regime, walls and how to trade them right now' },
];

// Default: no overlays until preferences sync (prevents flash of all overlays)
export const DEFAULT_VISIBLE_OVERLAYS: OverlayId[] = [];

interface OverlayLibraryState {
  visibleOverlays: OverlayId[];
  isOverlayVisible: (id: OverlayId) => boolean;
  showOverlay: (id: OverlayId) => void;
  hideOverlay: (id: OverlayId) => void;
  toggleOverlay: (id: OverlayId) => void;
  setVisibleOverlays: (overlays: OverlayId[]) => void;
  syncFromPreferences: () => void;
}

// Helper to persist to server — returns success/failure for rollback
const persistToServer = async (overlays: OverlayId[]): Promise<boolean> => {
  try {
    await updatePreferenceDirect('overlayLibrary', { visibleOverlays: overlays });
    return true;
  } catch (err) {
    console.error('Failed to persist overlay preferences:', err);
    return false;
  }
};

export const useOverlayLibraryStore = create<OverlayLibraryState>()(
  subscribeWithSelector((set, get) => ({
    visibleOverlays: DEFAULT_VISIBLE_OVERLAYS,

    isOverlayVisible: (id) => get().visibleOverlays.includes(id),

    showOverlay: (id) => {
      const current = get().visibleOverlays;
      if (!current.includes(id)) {
        const updated = [...current, id];
        set({ visibleOverlays: updated });
        persistToServer(updated).catch(() => {
          set({ visibleOverlays: current });
        });
      }
    },

    hideOverlay: (id) => {
      const current = get().visibleOverlays;
      if (current.includes(id)) {
        const updated = current.filter(o => o !== id);
        set({ visibleOverlays: updated });
        persistToServer(updated).catch(() => {
          set({ visibleOverlays: current });
        });
      }
    },

    toggleOverlay: (id) => {
      const current = get().visibleOverlays;
      const updated = current.includes(id)
        ? current.filter(o => o !== id)
        : [...current, id];
      set({ visibleOverlays: updated });
      persistToServer(updated).catch(() => {
        set({ visibleOverlays: current });
      });
    },

    setVisibleOverlays: (overlays) => set({ visibleOverlays: overlays }),

    syncFromPreferences: () => {
      const prefs = getPreferences();
      const saved = prefs.overlayLibrary?.visibleOverlays as OverlayId[] | undefined;
      if (saved && Array.isArray(saved)) {
        set({ visibleOverlays: saved });
      }
    },
  }))
);
